import { useState, useEffect, useRef, useMemo } from "react";
import useMaterialsMasterDataInit from "./useMaterialsMasterDataInit";
import { useMaterialUpdate } from "./useMaterialUpdate";
import { useDatabaseSync } from "./useDatabaseSync";
import { useFormulas, useInspectionAttributes, useProductDependencies } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { toLegacyDependencyRules } from "../../../../types/productDependencies";

export function useMaterials({ measurements }: { measurements: any }) {
    const { project } = useProject();
    const { useList } = useFormulas();
    const { data: formulas } = useList();
    const { useList: useListDependencies } = useProductDependencies();
    const { data: dependenciesConfig, isLoading: isDependenciesLoading } = useListDependencies();
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const listAttributesSvc = useListAttributes();
    const [attributes, setAttributes] = useState<any[]>([]);

    const legacyDependencies = useMemo(
        () => (dependenciesConfig ? toLegacyDependencyRules(dependenciesConfig) : []),
        [dependenciesConfig],
    );

    useEffect(() => {
        if (!project?.id) return;
        listAttributesSvc.mutateAsync({ ProjectId: project.id }).then(setAttributes).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    const [materialSchema, setMaterialSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [identifiedProducts, setIdentifiedProducts] = useState<any>(null);
    const [missingProducts, setMissingProducts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedValues, setSelectedValues] = useState<Record<string, any>>({});
    const [openAdditionalProductDialog, setOpenAdditionalProductDialog] = useState(false);

    const { initializeDbState, syncToDatabase } = useDatabaseSync();
    const syncReadyRef = useRef(false);

    useEffect(() => {
        if (!syncReadyRef.current) return;
        void syncToDatabase(selectedValues);
    }, [selectedValues]); // eslint-disable-line react-hooks/exhaustive-deps

    const { handleQuantityUpdate, handleClearProduct, handleProductChange, handleColorChange } = useMaterialUpdate({
        materialSchema,
        measurements,
        formulas,
        attributes,
        dependencies: legacyDependencies,
        setSelectedValues,
    });

    const { loadMasterData, fetchCartItems, calculatePriceRanges } = useMaterialsMasterDataInit({
        measurements,
        formulas,
        attributes,
        dependencies: legacyDependencies,
        materialSchema,
        setMaterialSchema,
        setCart,
        setPriceRanges,
        setIdentifiedProducts,
        setMissingProducts,
        setIsLoading,
        setSelectedValues: (values) => {
            setSelectedValues(values);
            initializeDbState(values);
            syncReadyRef.current = true;
        },
    });

    return {
        materialSchema,
        cart,
        priceRanges,
        identifiedProducts,
        missingProducts,
        isLoading: isLoading || isDependenciesLoading,
        selectedValues,
        openAdditionalProductDialog,
        setOpenAdditionalProductDialog,
        loadMasterData,
        fetchCartItems,
        calculatePriceRanges,
        handleQuantityUpdate,
        handleClearProduct,
        handleProductChange,
        handleColorChange,
    };
}
